import { ref, computed } from 'vue';

/**
 * Outcomes (v3 homepage) — the "After you submit" feed for the FeedbackStrip
 * widget. Lists the current user's recently submitted evaluations and their
 * outcome (submitted / approved / sent back) with the resolved level, from
 * `POST Report/outcomes`. `self` pattern; caller injects an axios-like `http`.
 *
 * Authored STATIC items always win (so a configured strip keeps working); only
 * when none are configured does it fetch the live feed. Either way it yields
 * `items` in the SAME shape the presentation renders: { title, detail, when,
 * status, reference }.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number} [options.getLimit] rows to show (default 6)
 * @param {() => Array}  [options.getStaticItems] authored items (override live)
 * @param {() => void}   [options.onReady] called once after the first load
 */
const SERVER_LIMIT = 12;

const relTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < 0) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (diff < 3600000) return 'Just now';
  if (diff < day) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export function outcomes(options) {
  const { http, getLimit = () => 6, getStaticItems = () => [], onReady = () => {} } = options || {};
  const limit = () => getLimit() || 6;
  const staticItems = () => {
    const s = getStaticItems();
    return Array.isArray(s) ? s : [];
  };

  const self = { rows: ref([]), loading: ref(false) };

  self.isLoading = computed(() => self.loading.value);

  // Outcome prose per status (prototype V3_RETURN tone: say what's HAPPENING,
  // not just the raw status), with the resolved level appended when known.
  const detailFor = (r, sel) => {
    const key = (r && r.status_key) || 'submitted';
    if (key === 'approved') return sel ? `Approved — Level ${sel} confirmed` : 'Approved';
    if (key === 'returned' || key === 're-review') return 'Sent back — needs your revision';
    // submitted / pending-* → in review
    const base = 'With the Reward team for review';
    return sel ? `${base} · Level ${sel} proposed` : base;
  };

  const toItem = (r) => {
    const sel = r && r.selected_level ? String(r.selected_level) : '';
    return {
      title: (r && (r.doc_title || r.doc_name || r.reference)) || '',
      detail: detailFor(r, sel),
      when: relTime(r && r.outcome_date),
      status: (r && r.status_key) || 'submitted',
      reference: (r && r.reference) || '',
    };
  };

  // Authored items override the live feed.
  self.items = computed(() => {
    const stat = staticItems();
    if (stat.length) return stat.slice(0, limit());
    return self.rows.value.map(toItem).slice(0, limit());
  });
  self.count = computed(() => self.items.value.length);

  async function load() {
    // Skip the round-trip entirely when the strip is statically configured.
    if (staticItems().length) return;
    self.loading.value = true;
    try {
      const resp = await http.post('Report/outcomes', { limit: SERVER_LIMIT });
      const data = resp && resp.data;
      self.rows.value = Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
